import { IndexedDBKv, KvCache, MemStorage, StorageKv, type KvDb } from "@package/util/kv";
import { defaultMonthsRangeSettings } from "@package/grid-utils";
import { simpleStableHash } from "@package/util/hash";
import type { GraphQLClient } from "@package/graphql";
import { timeout, all } from "@package/util/promise";
import { logger } from "@artis/logger";
import { nanoid } from "nanoid";
import * as R from "remeda";

import type {
  ConfigsQueryQuery,
  ConfigsSubSubscription,
  ProductInfoFragmentFragment,
  ProductInfoQuery as ProductInfoQueryType,
} from "../../__generated__/gql/graphql";
import { ConfigsQuery, ProductInfoFragment, ProductInfoQuery } from "../products/queries";
import { readFragment } from "../../graphql";

// Product meta for change detection
export type ProductMeta = {
  version: number;
  updatedAt: string;
  infoHash?: string;
  configHash?: string;
};

// Product configs
export type TProductConfig = {
  formula?: string | null;
  product: string;
  offset: number;
};

export type TProductConfigs = Record<string, TProductConfig[]>;

// Product info
export type ProductInfo = Record<string, ProductInfoFragmentFragment>;

// Products and configs cache
export type ProdAndConfigCacheItems = {
  meta: ProductMeta;
  info: ProductInfo;
  configs: TProductConfigs;
};
export type TProdAndConfigCache = KvDb<ProdAndConfigCacheItems>;

const sharedFallbackStorage = new MemStorage();

export class ProductConfigQueryCache {
  static INDEXED_DB_TIMEOUT = 2_000;
  private inner: Partial<ProdAndConfigCacheItems> = {};
  private userId: string | null = null;
  /** Bumped on load/clear so an in-flight refresh cannot commit after a user switch. */
  private session = 0;
  private cache: TProdAndConfigCache;
  // Tracks an in-flight load() for the current userId so concurrent callers share the same network fetch instead of each running their own (expensive) ProductInfoQuery + ConfigsQuery pair.
  private loading: Promise<Partial<ProdAndConfigCacheItems>> | null = null;

  constructor(cach?: TProdAndConfigCache) {
    const kv = typeof indexedDB !== "undefined" ? new IndexedDBKv() : new StorageKv(sharedFallbackStorage);
    this.cache =
      cach ??
      new KvCache<ProdAndConfigCacheItems>(kv, {
        version: 2,
        // If no data is written or accessed within 7 days, the cache is invalidated
        maxIdleTime: 7 * 24 * 3600_000,
      });
  }

  load = (graphql: GraphQLClient, userId: string): Promise<Partial<ProdAndConfigCacheItems>> => {
    if (this.inner.info && this.userId === userId) return Promise.resolve(this.inner);
    if (this.loading && this.userId === userId) return this.loading;
    this.userId = userId;
    this.session += 1;

    const loadPromise = (async () => {
      const [meta, info, configs] = await timeout(
        ProductConfigQueryCache.INDEXED_DB_TIMEOUT,
        all([this.cache.get("meta"), this.cache.get("info"), this.cache.get("configs")]),
      ).catch((err) => {
        logger.error("Failed to load product and configs from cache", err);
        this.cache.clear().catch((clearErr) => logger.error("Failed to clear corrupted cache", clearErr));
        return [null, null, null];
      });

      if (meta && info && configs) {
        this.inner = { meta, info, configs };
        return this.inner;
      }

      const span = logger.span("info", "product-and-configs-load");

      const [prodRes, configRes] = await Promise.all([
        graphql.execute(ProductInfoQuery, { folioUser: userId }),
        graphql.execute(ConfigsQuery, { maxRelMonth: defaultMonthsRangeSettings }),
      ]);

      span.exit();

      const i = transformProductInfoQuery(prodRes.data);
      const c = transformProductConfigsQuery(configRes.data);

      const m: ProductMeta = {
        version: Date.now(),
        updatedAt: new Date().toISOString(),
        infoHash: simpleStableHash(i),
        configHash: simpleStableHash(c),
      };
      this.inner = { meta: m, info: i, configs: c };

      await timeout(
        ProductConfigQueryCache.INDEXED_DB_TIMEOUT,
        all([this.cache.set("info", i), this.cache.set("configs", c), this.cache.set("meta", m)]),
      ).catch((err) => logger.error("Failed to set product and configs in cache", err));

      return this.inner;
    })();
    this.loading = loadPromise;
    loadPromise.finally(() => {
      if (this.loading === loadPromise) this.loading = null;
    });
    return loadPromise;
  };

  value = () => this.inner;

  /** Drop products the live query no longer returns, so the grid blanks without a full reload. */
  removeProducts = (ids: readonly string[]) => {
    if (!this.inner.info || ids.length === 0) return;
    let changed = false;
    const info = { ...this.inner.info };
    for (const id of ids) {
      if (id in info) {
        delete info[id];
        changed = true;
      }
    }
    if (!changed) return;

    const configs = this.inner.configs ? { ...this.inner.configs } : undefined;
    if (configs) for (const id of ids) delete configs[id];

    const meta = this.meta({
      infoHash: simpleStableHash(info),
      ...(configs ? { configHash: simpleStableHash(configs) } : {}),
    });
    this.inner = { ...this.inner, info, configs, meta };

    const writes = [this.cache.set("info", info), this.cache.set("meta", meta)];
    if (configs) writes.push(this.cache.set("configs", configs));
    timeout(ProductConfigQueryCache.INDEXED_DB_TIMEOUT, all(writes)).catch((err) =>
      logger.error("Failed to persist removed products to indexdb", err),
    );
  };

  subscribe = (
    graphql: GraphQLClient,
    onInfo: (info: ProductInfo) => void,
    onConfigs: (configs: TProductConfigs) => void,
  ) => {
    const infoSub = graphql.subscribe(
      ProductInfoQuery,
      { folioUser: this.userId ?? "" },
      {
        next: (data) => {
          const next = this.handleNewProductInfo(data.data);
          if (next) onInfo(next);
        },
      },
    );
    const configsSub = graphql.subscribe(
      ConfigsQuery,
      { maxRelMonth: defaultMonthsRangeSettings },
      {
        next: (data) => {
          const next = this.handleNewProductConfigs(data.data);
          if (next) onConfigs(next);
        },
      },
    );
    return () => {
      infoSub();
      configsSub();
    };
  };

  clear = () => {
    this.session += 1;
    this.inner = {};
    this.userId = null;
    return this.cache.clear();
  };

  // Forces a refetch of product info + configs from the server. Used after permissions change so that the cache is kept in sync with permissions.
  refresh = async (graphql: GraphQLClient) => {
    if (!this.userId) return null;
    const startedFor = this.userId;
    const session = this.session;

    const span = logger.span("info", "product-and-configs-refresh");

    const [prodRes, configRes] = await Promise.all([
      graphql.execute(ProductInfoQuery, { folioUser: startedFor }),
      graphql.execute(ConfigsQuery, { maxRelMonth: defaultMonthsRangeSettings }),
    ]);

    span.exit();

    if (this.userId !== startedFor || this.session !== session) return null;

    const i = transformProductInfoQuery(prodRes.data);
    const c = transformProductConfigsQuery(configRes.data);

    const m: ProductMeta = {
      version: Date.now(),
      updatedAt: new Date().toISOString(),
      infoHash: simpleStableHash(i),
      configHash: simpleStableHash(c),
    };
    this.inner = { meta: m, info: i, configs: c };

    await timeout(
      ProductConfigQueryCache.INDEXED_DB_TIMEOUT,
      all([this.cache.set("info", i), this.cache.set("configs", c), this.cache.set("meta", m)]),
    ).catch((err) => logger.error("Failed to persist refreshed product info to indexdb", err));

    if (this.userId !== startedFor || this.session !== session) return null;

    return this.inner;
  };

  private handleNewProductInfo = (info?: ProductInfoQueryType | null) => {
    if (!info) return null;
    const i = transformProductInfoQuery(info);
    const hash = simpleStableHash(i);
    if (hash === this.inner.meta?.infoHash) return null;
    const meta = this.meta({ infoHash: hash });
    this.inner = { ...this.inner, info: i, meta };

    timeout(
      ProductConfigQueryCache.INDEXED_DB_TIMEOUT,
      all([this.cache.set("info", i), this.cache.set("meta", meta)]),
    ).catch((err) => logger.error("Failed update product info in indexdb", err));

    return this.inner.info;
  };

  private handleNewProductConfigs = (configs?: ConfigsQueryQuery | null) => {
    if (!configs) return null;
    const c = transformProductConfigsQuery(configs);
    const hash = simpleStableHash(c);
    const meta = this.meta({ configHash: hash });
    this.inner = { ...this.inner, configs: c, meta };

    timeout(
      ProductConfigQueryCache.INDEXED_DB_TIMEOUT,
      all([this.cache.set("configs", c), this.cache.set("meta", meta)]),
    ).catch((err) => logger.error("Failed update product configs in indexdb", err));

    return this.inner.configs;
  };

  private meta = (p?: Partial<ProductMeta> | null) => {
    return {
      version: this.inner.meta?.version ?? Date.now(),
      updatedAt: this.inner.meta?.updatedAt ?? new Date().toISOString(),
      infoHash: this.inner.meta?.infoHash ?? "",
      configHash: this.inner.meta?.configHash ?? "",
      ...p,
    };
  };
}

const transformProductInfoQuery = (res?: ProductInfoQueryType | null) => {
  const frags = readFragment(ProductInfoFragment, res?.product) ?? [];
  return Object.fromEntries(frags.map((item) => [item.id, item]));
};

export const transformProductConfigsQuery = (
  res?: ConfigsQueryQuery | ConfigsSubSubscription | null,
): TProductConfigs =>
  R.pipe(
    res?.product_config ?? [],
    R.map((config) => ({
      formula: config?.formula,
      product: config.product,
      offset: config.offset,
    })),
    R.groupBy((config) => config.product),
  );

// UUIDv4 pattern used in formula strings to reference other products.
const PRODUCT_ID_REGEX = /[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi;

export const extractProductIdsFromFormula = (formula: string): string[] => {
  const matches = formula.match(PRODUCT_ID_REGEX);
  return matches ? matches.map((id) => id.toLowerCase()) : [];
};

// Returns a map of product id → set of offsets whose formula references a product the user can't access (directly or transitively at the same offset). Used to blank out individual grid cells without removing the containing product from the cache: each formula lives at one offset, so only that row should be blanked, while neighboring rows on the same product column may still resolve fine.
export const computeMissingCells = (info: ProductInfo, configs: TProductConfigs): Map<string, Set<number>> => {
  const missing = new Map<string, Set<number>>();
  const addMissing = (productId: string, offset: number) => {
    let offsets = missing.get(productId);
    if (!offsets) {
      offsets = new Set();
      missing.set(productId, offsets);
    }
    offsets.add(offset);
  };
  const isCellMissing = (productId: string, offset: number) => missing.get(productId)?.has(offset) ?? false;

  let changed = true;
  while (changed) {
    changed = false;
    for (const [productId, configList] of Object.entries(configs)) {
      if (!(productId in info)) continue;
      for (const config of configList) {
        if (!config.formula) continue;
        if (isCellMissing(productId, config.offset)) continue;
        const refs = extractProductIdsFromFormula(config.formula);
        const broken = refs.some((ref) => {
          if (ref === productId) return false;
          if (!(ref in info)) return true; // referenced product is inaccessible
          return isCellMissing(ref, config.offset); // referenced cell at the same offset is missing
        });
        if (broken) {
          addMissing(productId, config.offset);
          changed = true;
        }
      }
    }
  }
  return missing;
};

// Updating the query cache (i.e. for optimistic updates) when multiple tabs are open is buggy. So we set a unique key for each tab to avoid conflicts. sessionStorage is per-tab so we can isolate the cache for each tab.
export function getTabSessionKey() {
  if (!sessionStorage.getItem("tab_session_key")) {
    sessionStorage.setItem("tab_session_key", nanoid());
  }
  return sessionStorage.getItem("tab_session_key");
}
